if (!customElements.get('product-fixed-atc-bar')) {
  class ProductFixedAtcBar extends HTMLElement {
    constructor() {
      super();
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.onBarSubmitClick = this.onBarSubmitClick.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.updateTitleFade = this.updateTitleFade.bind(this);
      this.updateBottom = this.updateBottom.bind(this);
      this.mobileMq = window.matchMedia('(max-width: 768.98px)');
      this.topVisible = false;
      this.mainBelow = false;
    }

    connectedCallback() {
      this.mainSection = document.querySelector('.cc-main-product');
      this.productRoot = this.mainSection?.querySelector('.js-product');
      this.mainForm = this.mainSection?.querySelector('product-form');
      this.mainAddBtn = this.mainForm?.querySelector('button[name="add"]');
      this.mainPriceEl = this.mainSection?.querySelector('.product-info__price .price');
      this.barPrice = this.querySelector('.js-product-fixed-atc-price');
      this.barBtn = this.querySelector('.js-product-fixed-atc-submit');
      this.barImg = this.querySelector('.js-product-fixed-atc-img');
      this.barTitle = this.querySelector('.js-product-fixed-atc-title');
      this.barLabel = this.querySelector('.js-product-fixed-atc-label');
      this.barIcon = this.querySelector('.js-product-fixed-atc-icon');
      // Mobile bottom button lives next to the bar (outside it, so it can sit at the bottom)
      this.bottomWrap = this.parentElement?.querySelector('[data-atc-bottom]');
      this.bottomBtn = this.bottomWrap?.querySelector('.js-product-fixed-atc-bottom-submit');
      this.bottomLabel = this.bottomWrap?.querySelector('.js-product-fixed-atc-bottom-label');
      this.bottomIcon = this.bottomWrap?.querySelector('.js-product-fixed-atc-bottom-icon');
      this.bottomPrice = this.bottomWrap?.querySelector('.js-product-fixed-atc-bottom-price');
      this.bottomIsBar = this.bottomWrap?.dataset.style === 'bar';

      // Fade the end of the title when it doesn't fit in 2 lines
      if (this.barTitle) {
        if ('ResizeObserver' in window) {
          this.titleObserver = new ResizeObserver(this.updateTitleFade);
          this.titleObserver.observe(this.barTitle);
        }
        window.addEventListener('resize', this.updateTitleFade);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.updateTitleFade);
        this.updateTitleFade();
      }

      if (!this.mainForm || !this.barBtn) return;

      this.barBtn.addEventListener('click', this.onBarSubmitClick);
      this.productRoot?.addEventListener('on:variant:change', this.onVariantChange);

      if (this.mainAddBtn) {
        this.btnObserver = new MutationObserver(() => this.syncFromMain());
        this.btnObserver.observe(this.mainAddBtn, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      if (this.mainPriceEl) {
        this.priceObserver = new MutationObserver(() => this.syncFromMain());
        this.priceObserver.observe(this.mainPriceEl, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onIntersect, {
          root: null,
          threshold: 0,
          rootMargin: '0px 0px 0px 0px'
        });
        this.observer.observe(this.mainForm);
      }

      // Mobile bottom button: visible while the main add to cart button is still below the screen
      if (this.bottomBtn) {
        this.bottomBtn.addEventListener('click', this.onBarSubmitClick);
        if ('IntersectionObserver' in window) {
          this.bottomObserver = new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1];
            this.mainBelow = !entry.isIntersecting && entry.boundingClientRect.top > 0;
            this.updateBottom();
          });
          this.bottomObserver.observe(this.mainAddBtn || this.mainForm);
        }
        if (this.mobileMq.addEventListener) this.mobileMq.addEventListener('change', this.updateBottom);
        window.addEventListener('resize', this.updateBottom);
        // Chat widgets usually load after the page – lift them as soon as they appear
        this.widgetWatcher = new MutationObserver(() => {
          if (this.bottomWrap.classList.contains('is-visible')) this.liftWidgets(true);
        });
        this.widgetWatcher.observe(document.body, { childList: true });
        setTimeout(() => this.widgetWatcher?.disconnect(), 15000);
        // Hide the bottom button while the menu, cart or another drawer is open
        this.overlayWatcher = new MutationObserver(this.updateBottom);
        this.overlayWatcher.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        document.querySelectorAll('.main-menu__disclosure').forEach((el) => {
          this.overlayWatcher.observe(el, { attributes: true, attributeFilter: ['class', 'open'] });
        });
      }

      window.addEventListener('on:breakpoint-change', this.onBreakpointChange);

      this.syncFromMain();
    }

    disconnectedCallback() {
      window.removeEventListener('on:breakpoint-change', this.onBreakpointChange);
      window.removeEventListener('resize', this.updateTitleFade);
      this.productRoot?.removeEventListener('on:variant:change', this.onVariantChange);
      this.barBtn?.removeEventListener('click', this.onBarSubmitClick);
      this.observer?.disconnect();
      this.btnObserver?.disconnect();
      this.priceObserver?.disconnect();
      this.titleObserver?.disconnect();
      this.bottomObserver?.disconnect();
      this.widgetWatcher?.disconnect();
      this.overlayWatcher?.disconnect();
      this.bottomBtn?.removeEventListener('click', this.onBarSubmitClick);
      if (this.mobileMq.removeEventListener) this.mobileMq.removeEventListener('change', this.updateBottom);
      window.removeEventListener('resize', this.updateBottom);
      this.bottomWrap?.classList.remove('is-visible');
      if (this.widgetsLifted) this.liftWidgets(false);
      document.body.classList.remove('product-fixed-atc-bar--active');
    }

    updateTitleFade() {
      if (!this.barTitle) return;
      const cut = this.barTitle.scrollHeight > this.barTitle.clientHeight + 1;
      this.barTitle.classList.toggle('is-truncated', cut);
    }

    onBreakpointChange() {
      if (!this.allowBar()) {
        this.setVisible(false);
      }
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        const pastForm = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.setVisible(pastForm);
      });
    }

    allowBar() {
      if (this.dataset.hideOnMobile !== 'true') return true;
      if (typeof theme === 'undefined' || !theme.mediaQueries) return true;
      return window.matchMedia(theme.mediaQueries.md).matches;
    }

    /* True while the mobile menu, the cart drawer or another overlay is open */
    static overlayOpen() {
      const body = document.body;
      if (body.classList.contains('overflow-hidden') || body.classList.contains('fixed')) return true;
      return Boolean(document.querySelector('.main-menu__disclosure.is-open, .main-menu__disclosure[open]'));
    }

    updateBottom() {
      if (!this.bottomWrap) return;
      const show = this.mobileMq.matches && this.mainBelow && !this.topVisible && !ProductFixedAtcBar.overlayOpen();
      if (show) this.alignBottom();
      if (show === this.bottomShown) return;
      this.bottomShown = show;
      this.bottomWrap.classList.toggle('is-visible', show);
      this.liftWidgets(show);
    }

    /* Floating widgets in the bottom corner (e.g. the Chatty bubble) move up above the
       bottom button while it is shown, instead of overlapping it. Uses the CSS `translate`
       property so the widget's own transform animations keep working. */
    liftWidgets(show) {
      const selector = this.bottomWrap?.dataset.moveUp;
      if (!selector) return;
      let lift = 0;
      if (show) {
        // Bar style: lift above the whole bar; button style: above the floating button
        const ref = this.bottomIsBar ? this.bottomWrap : this.bottomBtn;
        lift = Math.round(ref.getBoundingClientRect().height + 12);
      }
      try {
        document.querySelectorAll(selector).forEach((el) => {
          if (this.contains(el) || this.bottomWrap.contains(el)) return;
          el.style.setProperty('transition', 'translate 0.25s ease');
          el.style.setProperty("translate",lift?`0 -${lift}px`:"0 0")
        });
      } catch (e) { /* invalid selector */ }
      this.widgetsLifted = Boolean(lift);
    }

    /* Same left/right edges as the main add to cart button */
    alignBottom() {
      if (this.bottomIsBar) return; // the bar is always full width
      const ref = this.mainAddBtn || this.mainForm;
      if (!ref || !this.bottomWrap) return;
      const rect = ref.getBoundingClientRect();
      if (!rect.width) return;
      const vw = document.documentElement.clientWidth;
      this.bottomWrap.style.setProperty('--atc-bottom-left', Math.round(rect.left) + 'px');
      this.bottomWrap.style.setProperty('--atc-bottom-right', Math.round(vw - rect.right) + 'px');
    }

    setVisible(visible) {
      const effective = Boolean(visible) && this.allowBar();
      this.topVisible = effective;
      this.updateBottom();
      this.classList.toggle('is-visible', effective);
      document.body.classList.toggle('product-fixed-atc-bar--active', effective);
      if (effective) {
        requestAnimationFrame(() => {
          this.syncFromMain();
          this.updateTitleFade();
        });
      }
    }

    onVariantChange(evt) {
      this.updateImage(evt.detail?.variant);
      requestAnimationFrame(() => this.syncFromMain());
    }

    updateImage(variant) {
      if (!this.barImg) return;
      const fallback = this.barImg.dataset.srcFallback;
      const src = variant?.featured_image?.src;
      if (src) {
        this.barImg.src = ProductFixedAtcBar.withWidth(src, 120);
        if (variant.featured_image.alt) {
          this.barImg.alt = variant.featured_image.alt;
        }
      } else if (fallback) {
        this.barImg.src = fallback;
      }
    }

    static withWidth(src, width) {
      if (!src || String(src).includes('width=')) return src;
      const sep = src.includes('?') ? '&' : '?';
      return `${src}${sep}width=${width}`;
    }

    syncFromMain() {
      if (this.mainPriceEl && this.barPrice) {
        this.barPrice.innerHTML = this.mainPriceEl.innerHTML;
        this.barPrice.className = this.mainPriceEl.className;
      }
      if (this.mainPriceEl && this.bottomPrice) {
        // Keep the .price wrapper so the bar layout (old price above) still applies
        this.bottomPrice.innerHTML = this.mainPriceEl.outerHTML;
      }
      if (this.mainAddBtn && this.barBtn) {
        this.barBtn.disabled = this.mainAddBtn.disabled;
        const aria = this.mainAddBtn.getAttribute('aria-disabled');
        if (aria === 'true') {
          this.barBtn.setAttribute('aria-disabled', 'true');
        } else {
          this.barBtn.removeAttribute('aria-disabled');
        }
        // Only the text is copied from the main button, so the bar keeps its own icon
        const label = this.mainAddBtn.textContent.replace(/\s+/g, ' ').trim();
        if (this.barLabel) {
          if (label) this.barLabel.textContent = label;
        } else if (label) {
          this.barBtn.textContent = label;
        }
        if (this.barIcon) this.barIcon.hidden = this.mainAddBtn.disabled;
        if (this.bottomBtn) {
          this.bottomBtn.disabled = this.mainAddBtn.disabled;
          if (this.bottomLabel && label) this.bottomLabel.textContent = label;
          if (this.bottomIcon) this.bottomIcon.hidden = this.mainAddBtn.disabled;
          this.bottomBtn.classList.toggle('is-loading', this.mainAddBtn.classList.contains('is-loading'));
        }
        this.barBtn.classList.toggle('is-loading', this.mainAddBtn.classList.contains('is-loading'));
      }
    }

    onBarSubmitClick(evt) {
      evt.preventDefault();
      if (!this.mainAddBtn || this.mainAddBtn.disabled) return;
      this.mainAddBtn.click();
    }
  }

  customElements.define('product-fixed-atc-bar', ProductFixedAtcBar);
}